{
  "account": {
    "fields": {
      "userId": "User ID",
      "webId": "Web ID",
      "userRole": "User Role",
      "accountType": "Account Type"
    },
    "sections": {
      "security": "Security"
    },
    "buttons": {
      "addPassword": "Add Password",
      "refresh": "Refresh",
      "logout": "Logout"
    },
    "messages": {
      "passwordEnabled": "Password authentication is enabled for your account.",
      "addPasswordPrompt": "Add a password to enable password-based login alongside your SSO method."
    },
    "accountTypes": {
      "admin": "Admin",
      "fboUser": "FBO User"
    },
    "addPasswordDialog": {
      "title": "Add Password Authentication",
      "successMessage": "Password added successfully! You can now log in with your email and password.",
      "description": "Add a password to your account ({{userEmail}}) to enable password-based login alongside your existing SSO method.",
      "labels": {
        "newPassword": "New Password",
        "confirmPassword": "Confirm Password",
        "strength": "Strength: {{strength}}"
      },
      "validationErrors": {
        "tooShort": "Password must be at least 8 characters",
        "needsUppercase": "Password must contain at least one uppercase letter",
        "needsLowercase": "Password must contain at least one lowercase letter",
        "needsNumber": "Password must contain at least one number",
        "noMatch": "Passwords do not match",
        "setFailed": "Failed to set password",
        "genericError": "An error occurred"
      },
      "strength": {
        "tooShort": "Too short",
        "weak": "Weak",
        "fair": "Fair",
        "good": "Good",
        "strong": "Strong"
      },
      "requirements": "Must be 8+ characters with uppercase, lowercase, and numbers",
      "buttons": {
        "cancel": "Cancel",
        "adding": "Adding...",
        "addPassword": "Add Password",
        "close": "Close"
      }
    },
    "roleSwitcher": {
      "title": "Role Testing (Admin Only)",
      "description": "Test the application as different user roles to verify permissions and UI behavior. This is a client-side only override that doesn't affect the database.",
      "selectLabel": "Test as Role",
      "activeMessage": "Role testing is active. Use the floating button (bottom-right) to switch roles or exit.",
      "buttonStartTesting": "Start Testing as {{role}}"
    },
    "languageSwitcher": {
      "title": "Language Testing (Admin Only)",
      "description": "Enable a floating button for quick language switching to test translations.",
      "buttonEnable": "Enable Language Switcher",
      "buttonDisable": "Disable Language Switcher",
      "activeMessage": "Language testing is active. Use the floating button (bottom-right) to switch languages."
    }
  },
  "configuration": {
    "sections": {
      "personalPreferences": "Personal Preferences",
      "globalPermissions": "Global Permissions",
      "rolePermissions": "Role Permissions",
      "emailIngestion": "Email Ingestion Address"
    },
    "descriptions": {
      "personalPreferences": "Customize your personal display and behavior preferences.",
      "globalPermissions": "Toggle global permissions. These will be applied to all users, regardless of role.",
      "rolePermissions": "Toggle permissions for each user role. These permissions will be applied to all users in each role.",
      "emailIngestion": "Forward trip schedules and reservations to this email address for automatic import."
    },
    "emailIngestion": {
      "copied": "Email address copied to clipboard",
      "copyTooltip": "Copy to clipboard"
    },
    "personalPreferences": {
      "darkMode": "Dark Mode",
      "hideLockReminder": "Hide Locked Aircraft Reminder",
      "showAircraftLabels": "Show Aircraft Labels",
      "language": "Language"
    },
    "globalPermissions": {
      "excludeObstructions": "Exclude obstructions, safety perimeters, and no-tow areas?",
      "forceDisplayNotes": "Notes in \"View Settings\" shows all entered note text",
      "enableScheduling": "Enable Scheduling Features",
      "displayDetailedDecisionLog": "Display Detailed Decision Dialog",
      "airportCode": "Airport Code",
      "timezone": "Timezone",
      "useListHoldingArea": "Use List-based Holding Area",
      "enableAutoParking": "Enable Auto Parking Features",
      "enableOpsCenter": "Enable Ops Center",
      "useMetricSystem": "Use Metric System"
    },
    "tooltips": {
      "excludeObstructions": "By default, AirPlx uses the hangar width & depth as total hangar square footage for utilization calcs. You can exclude obstructions (offices, cages, etc) and safety perimeters (no-tow areas) if desired.",
      "enableScheduling": "Show trip scheduling functionality including schedule pages and timeline views. When disabled, scheduling features will be hidden from the UI.",
      "displayDetailedDecisionLog": "Show detailed statistics, decision log tab, and performance metrics in AutoPark results dialog. For development and debugging purposes.",
      "airportCode": "ICAO or IATA airport code for this FBO (e.g., KBZN, KJFK). Used for FlightAware flight tracking in Ops Center.",
      "timezone": "IANA timezone for this FBO location. Used for converting local times in email ingestion (e.g., when FBO One sends arrival times in local time).",
      "useListHoldingArea": "When enabled, uses modern list-based holding area. When disabled, uses canvas-based visual holding area.",
      "enableAutoParking": "When enabled, auto parking features become available. Note: Both this setting AND the individual ramp's AutoParking toggle (in Ramp Editor) must be enabled to use auto parking features.",
      "enableOpsCenter": "Show Ops Center in navigation for system administrators. Ops Center provides real-time flight tracking, arrival/departure monitoring, and CSR workflow tools. When disabled, Ops Center will be hidden from the navigation menu."
    },
    "permissions": {
      "displayEstimatedValue": {
        "label": "Display Estimated Value",
        "description": "Display estimated value on the dashboard."
      },
      "addTeamMembers": {
        "label": "Add Team Members",
        "description": "Add team members to the FBO."
      },
      "modifyTenants": {
        "label": "Modify Tenants",
        "description": "Manage aircraft registrations and tenant information."
      },
      "modifyLayout": {
        "label": "Modify Layout",
        "description": "Modify layout elements (spots, zones, obstacles, reference images, calibration points, markings, doors, bays, stacks)."
      },
      "createTrips": {
        "label": "Create Trips",
        "description": "Create new trips and reservations."
      },
      "deleteTrips": {
        "label": "Delete Trips",
        "description": "Delete trips and reservations."
      },
      "useFavorites": {
        "label": "Use Favorites",
        "description": "Use the favorites feature to save, load, and edit favorite stacks."
      },
      "viewOnly": {
        "label": "View Only",
        "description": "View-only mode: User can view all data but cannot create, modify, or delete anything."
      },
      "modifyRates": {
        "label": "Modify Rates",
        "description": "Modify pricing rates and benchmark rates."
      },
      "modifyInfrastructure": {
        "label": "Modify Infrastructure",
        "description": "Modify core infrastructure (hangars, ramps, equipment)."
      },
      "modifyConfiguration": {
        "label": "Modify Configuration",
        "description": "Modify FBO configuration settings (service display, ops note tags)."
      }
    },
    "roles": {
      "fboAdmin": "FBO Admin",
      "manager": "Manager",
      "operator": "Operator",
      "csr": "CSR"
    },
    "limitedAccess": {
      "title": "Limited Access",
      "message": "You're logged in as {{role}}. Only FBO Admins can view and edit role permissions. Contact your administrator if you need access."
    },
    "placeholders": {
      "airportCode": "KBZN",
      "timezone": "Select timezone..."
    },
    "vipTags": {
      "title": "VIP Tags",
      "description": "Configure VIP tags for classifying base tenants. Tags will appear as colored badges on aircraft and trips.",
      "loading": "Loading VIP tags...",
      "noTags": "No VIP tags configured. Add your first tag below.",
      "addNew": "Add New Tag",
      "fields": {
        "label": "Tag label",
        "newLabel": "New tag label (e.g., VIP, YC)"
      },
      "buttons": {
        "add": "Add Tag"
      },
      "colorPicker": {
        "chooseColor": "Choose a color",
        "customColor": "Custom color..."
      },
      "success": {
        "created": "VIP tag created successfully",
        "deleted": "VIP tag deleted successfully"
      },
      "errors": {
        "loadFailed": "Failed to load VIP tags",
        "createFailed": "Failed to create VIP tag",
        "updateFailed": "Failed to update VIP tag",
        "deleteFailed": "Failed to delete VIP tag",
        "reorderFailed": "Failed to reorder VIP tags"
      },
      "errorTitle": "Error loading VIP Tags",
      "errorDescription": "An error occurred while loading VIP tag settings. Please try refreshing the page."
    }
  },
  "userManagement": {
    "search": {
      "placeholder": "Search by email"
    },
    "table": {
      "headers": {
        "name": "Name",
        "email": "Email",
        "role": "Role"
      }
    },
    "users": {
      "anonymous": "anonymous",
      "noName": "---",
      "airplx": "AirPlx"
    },
    "buttons": {
      "delete": "Delete"
    },
    "deleteDialog": {
      "title": "Delete User",
      "message": "Are you sure you want to delete {{userName}}?",
      "confirm": "Yes. Delete this user"
    },
    "errors": {
      "noPermissionModifyRoles": "You don't have permission to modify user roles.",
      "cannotChangeOwnRole": "You can't change your own role.",
      "cannotChangeAirplxRole": "You can't change the role of AirPlx employees.",
      "failedToChangeRole": "Failed to change role. Please try again.",
      "cannotDeleteSelf": "You can't delete yourself.",
      "cannotDeleteAirplx": "You can't delete AirPlx employees.",
      "failedToDelete": "Failed to delete user. Please try again."
    },
    "success": {
      "roleChanged": "Successfully changed role for {{userName}} to {{roleName}}",
      "userDeleted": "Successfully deleted {{userName}}",
      "copiedToClipboard": "Copied to clipboard"
    },
    "invitations": {
      "title": "Invites",
      "noInvites": "No invites outstanding",
      "sentOn": "Sent on {{date}} at {{time}}",
      "buttons": {
        "copyLink": "Copy Invite Link",
        "delete": "Delete Invite",
        "addTeamMember": "Add Team Member",
        "invite": "Invite"
      },
      "dialog": {
        "title": "Invite",
        "instructions": "Invite a new user to AirPlx. Enter their email address below and they'll recieve an email with instructions on how to join your team.",
        "successMessage": "There should be an email in {{email}}'s inbox with an invite.",
        "alternativeLink": "Alternatively, you can send the user this link directly:",
        "emailLabel": "Email Address",
        "roleLabel": "Role"
      },
      "emailWarning": {
        "title": "Personal E-mail Warning",
        "message": "You're adding a team member using an email address outside of your organization. Using a personal email (Gmail, iCloud, etc) is allowed, but remember to remove the user's AirPlx access in \"Settings\" if needed later on."
      }
    }
  },
  "integrations": {
    "title": "Integrations",
    "subtitle": "Connect AirPlx with external flight operations systems to automatically sync trip schedules and reservations.",
    "status": {
      "notConfigured": "Not Configured",
      "disabled": "Disabled",
      "enabled": "Enabled"
    },
    "lastSync": {
      "never": "Never",
      "justNow": "Just now",
      "minutesAgo": "{{count}} minute ago",
      "hoursAgo": "{{count}} hour ago"
    },
    "flightBridge": {
      "name": "FlightBridge",
      "subtitle": "Flight operations management",
      "description": "Sync trip schedules, aircraft details, and concierge reservations automatically from FlightBridge's flight operations platform.",
      "tooltips": {
        "credentialsVerified": "Credentials verified",
        "cannotConnect": "Cannot connect - check credentials"
      },
      "labels": {
        "companyId": "Company ID:",
        "airportCodes": "Airport Codes:",
        "lastSync": "Last Sync:",
        "error": "Error:"
      },
      "buttons": {
        "configure": "Configure",
        "setUp": "Set Up",
        "disable": "Disable"
      },
      "disableDialog": {
        "title": "Disable FlightBridge Integration?",
        "message": "Syncing will stop immediately. You can re-enable at any time from this page.",
        "confirm": "Disable"
      },
      "configDialog": {
        "title": "FlightBridge Configuration",
        "tabs": {
          "basicConfig": "Basic Config",
          "locationMapping": "Location Mapping"
        },
        "fields": {
          "companyId": {
            "label": "Company ID",
            "helper": "Your FlightBridge CompanyIdentifier"
          },
          "apiEnvironment": {
            "label": "API Environment",
            "production": "Production API (https://api.flightbridge.com)",
            "test": "Test API (https://testapi.flightbridge.com)"
          },
          "airportCodes": {
            "label": "Airport Codes",
            "placeholder": "KJFK, JFK",
            "helper": "Comma-separated ICAO/IATA codes. Only trips arriving/departing from these airports will be imported."
          },
          "username": {
            "label": "Username"
          },
          "password": {
            "label": "Password"
          },
          "enabled": {
            "label": "Enabled",
            "tooltip": "Enable FlightBridge integration to automatically sync trip schedules and reservations. When enabled, trips will be imported from FlightBridge and appear in your schedule."
          },
          "syncLocationUpdates": {
            "label": "Sync location updates back to FlightBridge",
            "tooltip": "When aircraft are moved in AirPlx (AutoPark, manual moves, etc.), automatically update their location in FlightBridge. Updates happen in the background within 1-5 seconds."
          }
        },
        "buttons": {
          "test": "Test Connection & Preview Data",
          "testing": "Testing...",
          "viewPreview": "View Data Preview",
          "save": "Save",
          "saving": "Saving...",
          "cancel": "Cancel"
        },
        "messages": {
          "testSuccess": "Authentication successful! Found {{totalCount}} reservations ({{filteredCount}} matching your airport codes). Click \"View Data Preview\" to see details.",
          "testFailed": "Test failed",
          "testInfo": "Click \"Test Connection & Preview Data\" to verify your FlightBridge credentials and see a sample of available data before saving.",
          "airportCodesRequired": "Airport codes are required",
          "saveFailed": "Failed to save configuration"
        },
        "locationMapping": {
          "loading": "Loading location mappings...",
          "emptyState": "No FlightBridge locations discovered yet. Click \"Test Connection & Preview Data\" in the Basic Config tab to discover locations from your FlightBridge account.",
          "description": "Map FlightBridge locations to your AirPlx hangars and ramps. These mappings will be used when syncing concierge reservations.",
          "unmappedWarning": "{{count}} location not yet mapped. Reservations for unmapped locations will not have a hangar or ramp assigned.",
          "table": {
            "headers": {
              "flightBridgeLocation": "FlightBridge Location",
              "airplxLocation": "AirPlx Location",
              "status": "Status"
            }
          },
          "menu": {
            "notMapped": "Not mapped",
            "hangars": "Hangars",
            "ramps": "Ramps"
          },
          "status": {
            "mapped": "Mapped",
            "unmapped": "Unmapped"
          },
          "help": "Locations are discovered automatically when you test the FlightBridge connection. Changes are saved immediately when you select a mapping.",
          "failedSync": "{{count}} location update failed to sync to FlightBridge",
          "retry": "Retry",
          "retrying": "Retrying...",
          "errors": {
            "failedToLoad": "Failed to load location mappings",
            "failedToSave": "Failed to save mapping"
          }
        }
      },
      "dataPreviewDialog": {
        "title": {
          "success": "FlightBridge Test Results",
          "failed": "FlightBridge Test Failed"
        },
        "auth": {
          "failed": "Authentication failed:",
          "success": "Successfully authenticated"
        },
        "errorDetails": {
          "title": "Technical Error Details",
          "message": "Message:",
          "code": "Code:",
          "httpStatus": "HTTP Status:"
        },
        "stats": {
          "dateRange": "Date Range",
          "totalReservations": "Total Reservations"
        },
        "table": {
          "title": "Sample Reservations (showing up to 10)",
          "headers": {
            "reservationId": "Reservation ID",
            "tailNumber": "Tail Number",
            "locationOnField": "Location on Field",
            "arrivalTime": "Arrival Time",
            "departureTime": "Departure Time",
            "status": "Status",
            "workflow": "Workflow",
            "services": "Services"
          },
          "values": {
            "notProvided": "Not provided",
            "unknown": "Unknown",
            "none": "None"
          }
        },
        "workflow": {
          "cancelled": "Cancelled",
          "requested": "Requested",
          "inProgress": "In progress",
          "completed": "Completed",
          "archived": "Archived",
          "rejected": "Rejected",
          "unknown": "Unknown",
          "notApplicable": "—"
        },
        "noData": {
          "title": "No reservations found for your company in the last 7 days. This could mean:",
          "reasons": {
            "noReservations": "No concierge reservations have been created",
            "outsideWindow": "All reservations are outside the 7-day window",
            "incorrectId": "The Company ID might be incorrect"
          }
        },
        "errors": {
          "title": "Errors"
        },
        "footer": {
          "duration": "Test completed in {{duration}}ms",
          "close": "Close"
        }
      }
    }
  },
  "opsCenter": {
    "airportConfig": {
      "title": "Airport Configuration",
      "subtitle": "Configure the airport code and name for this FBO's Ops Center.",
      "icaoLabel": "ICAO Code",
      "icaoPlaceholder": "KBZN",
      "icaoHelper": "The 4-letter ICAO airport identifier (e.g., KBZN, KJFK)",
      "nameLabel": "Airport Name",
      "namePlaceholder": "Bozeman Yellowstone International",
      "nameHelper": "Display name for the airport"
    },
    "notifications": {
      "title": "Notification Settings",
      "subtitle": "Configure which notifications you receive in Ops Center.",
      "arrivingSoon": "Arriving Soon",
      "etaChanges": "ETA Changes",
      "landed": "Landed",
      "departed": "Departed",
      "alertLeadTime": "Alert Lead Time",
      "leadTime15": "15 minutes",
      "leadTime30": "30 minutes",
      "leadTime60": "60 minutes"
    },
    "uiPreferences": {
      "title": "UI Preferences",
      "subtitle": "Configure the Ops Center user interface.",
      "showLivePositions": "Show Live Aircraft Positions"
    },
    "noteTags": {
      "title": "Note Tags",
      "subtitle": "Manage custom tags for flight notes.",
      "loading": "Loading tags...",
      "placeholder": "New tag name",
      "addButton": "Add Tag",
      "success": {
        "added": "Tag added successfully",
        "removed": "Tag removed successfully"
      },
      "errors": {
        "addFailed": "Failed to add tag",
        "removeFailed": "Failed to remove tag"
      }
    },
    "serviceDisplay": {
      "title": "Service Display",
      "subtitle": "Configure which services are displayed as badges and their order.",
      "loading": "Loading service configuration...",
      "sections": {
        "primaryBadges": "Primary Badges",
        "primaryDescription": "These services are displayed as badges on flight rows. Drag to reorder.",
        "otherServices": "Other Services",
        "otherDescription": "These services are available but not shown as badges."
      },
      "fields": {
        "label": "Display label"
      },
      "buttons": {
        "remove": "Remove",
        "add": "Add"
      },
      "services": {
        "fuel": "Fuel",
        "hangar": "Hangar",
        "catering": "Catering",
        "hotel": "Hotel",
        "crewCar": "Crew Car",
        "limo": "Limo",
        "carRental": "Car Rental",
        "arrivalLimo": "Arrival Limo",
        "departureLimo": "Departure Limo",
        "deicing": "Deicing",
        "groundPower": "Ground Power",
        "gpu": "GPU",
        "lavatory": "Lavatory",
        "water": "Water",
        "towing": "Towing",
        "oxygen": "Oxygen",
        "conferenceRoom": "Conference Room",
        "customs": "Customs",
        "payment": "Payment",
        "dishes": "Dishes",
        "linens": "Linens",
        "otherArrival": "Other (Arrival)",
        "otherDeparture": "Other (Departure)",
        "cip": "Cip",
        "confRoomShort": "Conf Room Short",
        "lav": "Lav"
      },
      "success": {
        "movedToPrimary": "Service moved to primary badges",
        "movedToOther": "Service moved to other services"
      },
      "errors": {
        "loadFailed": "Failed to load service configuration",
        "updateFailed": "Failed to update service",
        "reorderFailed": "Failed to reorder services"
      }
    }
  },
  "hangarSettings": {
    "title": "Base Tenants for {{hangarName}}",
    "table": {
      "headers": {
        "name": "Hangar Name",
        "dimensions": "Dimensions",
        "wallSpacing": "Wall Spacing",
        "baseTenants": "Base Tenants"
      }
    },
    "buttons": {
      "modify": "Modify",
      "delete": "Delete",
      "copy": "Copy",
      "cancel": "Cancel",
      "uploadImage": "Upload Reference Image",
      "cancelSetup": "Cancel Setup",
      "saveHangar": "Save Hangar"
    },
    "copyDialog": {
      "title": "Copy Hangar",
      "fboLabel": "Copy to FBO",
      "copyButton": "Copy Hangar"
    },
    "deleteDialog": {
      "title": "Are you sure you want to delete {{hangarName}}?",
      "message": "This hangar and all of its tenant info will be deleted. Any base tenants for this hangar will become unassigned.",
      "confirmButton": "Delete Hangar"
    },
    "emptyState": {
      "title": "No Hangars Configured",
      "description": "Click 'Add Hangar' above to configure your first hangar."
    },
    "tabs": {
      "basicInfo": "Basic Info",
      "dimensions": "Dimensions",
      "heightBays": "Height & Bays",
      "features": "Features"
    },
    "titles": {
      "editHangar": "Edit Hangar: {{name}}"
    },
    "confirmations": {
      "saveChanges": {
        "title": "Save Changes?",
        "message": "Are you sure you want to save these changes?"
      }
    },
    "labels": {
      "referenceImage": "Reference Image",
      "display": "Display?",
      "untitled": "Untitled",
      "autostackingEnabled": "Autostacking Enabled?"
    },
    "success": {
      "saved": "Hangar changes saved."
    },
    "errors": {
      "unknown": "Unknown error",
      "saveFailed": "Failed to save: {{error}}"
    },
    "dimensionsForm": {
      "labels": {
        "hangarName": "Hangar Name",
        "width": "Width ({{unit}})",
        "depth": "Depth ({{unit}})",
        "grossSquareFootage": "Gross Square Footage",
        "usableSquareFootage": "Usable Square Footage",
        "doorPockets": "Door Pockets",
        "leftDoorLength": "Left Door Length ({{unit}})",
        "rightDoorLength": "Right Door Length ({{unit}})",
        "doorDimensions": "Door Dimensions",
        "doorWidth": "Width ({{unit}})",
        "heightConfiguration": "Height Configuration",
        "useBayHeights": "Use bay-specific heights",
        "height": "Height ({{unit}})",
        "garageDoors": "Garage Doors",
        "noGarageDoors": "No garage doors. Click Add to create one.",
        "wall": "Wall",
        "wallLeft": "Left",
        "wallBack": "Back",
        "wallRight": "Right",
        "garageDoorWidth": "Width",
        "obstacles": "Obstacles",
        "wallSpacing": "Wall Spacing",
        "leftWall": "Left Wall ({{unit}})",
        "backWall": "Back Wall ({{unit}})",
        "rightWall": "Right Wall ({{unit}})"
      },
      "obstacleHint": "Use the toolbar above the canvas to add rectangle or polygon obstacles.",
      "tooltips": {
        "bayHeights": "Enable for stepped-eave hangars with different height zones. Each bay can have its own eave and clear height.",
        "eaveHeight": "Eave height for uniform hangars. This is the vertical clearance from floor to the lowest point of the roof structure."
      }
    },
    "bayEditor": {
      "orientations": {
        "lateral": "Lateral (Side-by-Side)",
        "longitudinal": "Longitudinal (Front-to-Back)"
      },
      "tooltips": {
        "lateral": "┌──┬───┬──┐\n│A │ B │C │\n│  │   │  │\n└──┴───┴──┘\nBays divide left→right\nExample: 3 bays of 80ft, 100ft, 80ft",
        "longitudinal": "┌─────────┐\n│    A    │\n├─────────┤\n│    B    │\n└─────────┘\nBays divide front→back\nExample: 2 bays of 60ft, 60ft",
        "bayRange": "Bay covers {{start}}{{unit}} - {{end}}{{unit}}",
        "baySegment": "Bay {{index}}: {{start}}-{{end}}{{unit}} ({{percent}}%)"
      },
      "labels": {
        "width": "Width ({{unit}})",
        "depth": "Depth ({{unit}})",
        "eaveHeight": "Eave Ht ({{unit}})",
        "clearHeight": "Clear Ht ({{unit}})"
      },
      "coverage": {
        "total": "Total: {{total}} {{unit}} / {{hangar}} {{unit}} ({{percent}}%)",
        "complete": "✓ Complete coverage",
        "exceeds": "⚠️ Exceeds by {{gap}}{{unit}}",
        "missing": "⚠️ Missing {{gap}}{{unit}}",
        "uncovered": "Uncovered {{dimension}}: {{start}}{{unit}} - {{end}}{{unit}}",
        "overflowIndicator": "+{{gap}}{{unit}}"
      },
      "buttons": {
        "addBay": "Add Bay",
        "fillRemainder": "Fill Remainder ({{gap}}{{unit}})"
      }
    },
    "newAircraftForm": {
      "labels": {
        "ownerManager": "Owner/Manager",
        "type": "Type",
        "notes": "Notes",
        "unassigned": "Unassigned",
        "notHangared": "Not Hangared"
      },
      "types": {
        "base": "Base",
        "transient": "Transient",
        "yellowCard": "Base + Yellow Card"
      },
      "placeholders": {
        "departureTime": "Departure Time: 05/24/2025 12:00"
      }
    },
    "obstacleContextMenu": {
      "copy": "Copy",
      "delete": "Delete"
    },
    "obstacleToolbar": {
      "addObstacleTooltip": "Add Obstacle",
      "addObstacleButton": "Add Obstacle",
      "rightClickHint": "right-click to delete an obstacle",
      "dialogTitle": "Add Obstacle/No Tow Area",
      "polygonDialogTitle": "Add Polygon Obstacle",
      "tools": {
        "select": "Select",
        "rectangle": "Rectangle",
        "polygon": "Polygon",
        "circle": "Circle"
      },
      "polygonHints": {
        "clickToAddPoints": "Click to add points",
        "minThreePoints": "Add at least 3 points",
        "clickFirstPointToClose": "Click first point to close polygon"
      },
      "circleHints": {
        "clickToPlaceCenter": "Click to place center point"
      },
      "circleDialogTitle": "Add Circle Obstacle",
      "circleInfo": "Circle Info",
      "centerPosition": "Center",
      "polygonInfo": "Polygon Info",
      "pointCount": "Points",
      "boundingBox": "Bounding Box",
      "heightHint": "Height is optional. Set it if the obstacle has height restrictions for aircraft placement.",
      "obstacleTypes": {
        "obstruction": "obstruction",
        "noTowArea": "no tow area",
        "unobstructive": "unobstructive",
        "caution": "caution"
      },
      "labels": {
        "type": "Type",
        "width": "Width ({{unit}})",
        "depth": "Depth ({{unit}})",
        "height": "Height ({{unit}})",
        "diameter": "Diameter ({{unit}})"
      },
      "buttons": {
        "cancel": "Cancel",
        "add": "Add"
      }
    },
    "bulkAdd": {
      "tabs": {
        "byMakeModel": "Add by Make and Model",
        "byTailNumbers": "Add by Tail Numbers"
      },
      "labels": {
        "searchByMakeModel": "Search by make and model",
        "numAircraft": "# Aircraft",
        "tailNumbers": "Tail Numbers",
        "selectAircraftType": "Select aircraft type"
      },
      "placeholders": {
        "tailNumbersMultiline": "N12345\nN54321\nN67890",
        "tailNumbersComma": "i.e. N12345, N54321, N67890"
      },
      "buttons": {
        "search": "Search",
        "addAircraft": "Add Aircraft",
        "remove": "Remove",
        "addMoreTailNumbers": "Add More Tail Numbers",
        "add": "Add"
      },
      "messages": {
        "enterOneTailPerLine": "Enter one tail number per line.",
        "allTailsFound": "✓ All tail numbers found in database",
        "unknownTailNumbersTitle": "Unknown Tail Numbers",
        "unknownTailNumbersInstructions": "The following tail numbers were not found in the database. Please select the aircraft type for each or remove them:",
        "aircraftAddedToList": "✓ Aircraft have been added to the list below",
        "maxAircraftWarning": "Maximum number of aircraft to add is 20.",
        "existingTailNumberWarning": "Aircraft with tail number {{tailNumber}} already exists. We've added the existing tail number and associated aircraft model.",
        "tailNumbersOptional": "Optional. Separate tail numbers with commas."
      },
      "table": {
        "headers": {
          "tailNumber": "Tail #",
          "model": "Model",
          "type": "Type",
          "direction": "Direction"
        }
      }
    },
    "tenantSelector": {
      "table": {
        "headers": {
          "lastHangared": "Last Hangared"
        }
      },
      "search": {
        "placeholder": "Search by tenant name or Tail #"
      },
      "buttons": {
        "removeAircraft": "Remove Aircraft",
        "addAircraft": "Add Aircraft"
      },
      "status": {
        "inLocation": "In {{locationName}}"
      },
      "dialog": {
        "confirmTitle": "Please Confirm"
      },
      "messages": {
        "aircraftAdded": "{{model}} added to {{locationName}}"
      }
    },
    "autocompleteAircraft": {
      "defaultLabel": "Aircraft",
      "suggested": "(suggested)",
      "noOptionsButton": "No aircraft found • Click here to request a new aircraft",
      "newAircraftRequest": {
        "title": "New Aircraft Request",
        "successTitle": "Request Received",
        "labels": {
          "manufacturer": "Manufacturer",
          "model": "Model",
          "uniqueModifiers": "Unique Modifiers (e.g. winglets, variations)",
          "timeframe": "Timeframe"
        },
        "messages": {
          "successMessage": "Thanks! We'll start working on this as soon as we can. We might email you if we have questions about the specifics of the aircraft you'd like to add so please watch your inbox.",
          "uploadTimeframe": "Allow 2-4 days for upload of new aircraft models. We will notify once it is available."
        }
      }
    },
    "addTripTab": {
      "labels": {
        "ownerManager": "Owner/Manager",
        "type": "Type",
        "notes": "Notes",
        "arrivalDateTime": "Arrival Date & Time",
        "departureDateTime": "Departure Date & Time",
        "noDeparture": "No departure"
      },
      "placeholders": {
        "ownerManager": "e.g., John Smith",
        "notes": "Optional notes about this trip..."
      },
      "tenantTypes": {
        "transient": "Transient",
        "base": "Base",
        "yellowCard": "Base + Yellow Card"
      },
      "messages": {
        "noDepartureInfo": "This aircraft will remain at the FBO indefinitely with no scheduled departure.",
        "existingTrips": "Existing trips for {{tailNumber}}:",
        "avoidOverlap": "Please select different dates to avoid overlap",
        "checkDatesWarning": "Check dates to avoid overlaps - we won't allow overlapping trips",
        "autoAssignedInfo": "This trip will be automatically assigned to the current {{locationType}}.",
        "overlapError": "Another overlapping trip exists. Please refresh and select different dates."
      }
    },
    "allTripsTab": {
      "labels": {
        "unassigned": "Unassigned",
        "startDate": "Start Date",
        "endDate": "End Date"
      },
      "placeholders": {
        "filterTailOrOwner": "Filter tail or owner"
      },
      "status": {
        "cancelled": "Cancelled",
        "past": "Past",
        "active": "Active",
        "future": "Future",
        "today": "Today",
        "tomorrow": "Tomorrow",
        "thisWeek": "This Week",
        "nextWeek": "Next Week",
        "thisMonth": "This Month"
      },
      "columns": {
        "tailNumber": "Tail Number",
        "arrival": "Arrival",
        "departure": "Departure",
        "duration": "Duration",
        "status": "Status",
        "location": "Location",
        "actions": "Actions"
      },
      "buttons": {
        "hidePast": "Hide Past",
        "showPast": "Show Past",
        "delete": "Delete"
      },
      "dialogs": {
        "deleteTitle": "Delete Trips?"
      },
      "errors": {
        "loadFailed": "Failed to load trips",
        "selectLocation": "Please select a location from the filter dropdown before assigning trips",
        "locationNotFound": "Selected location not found",
        "assignFailed": "Failed to assign trips",
        "unassignFailed": "Failed to unassign trips",
        "cancelFailed": "Failed to cancel trips",
        "deleteFailed": "Failed to delete trips"
      }
    },
    "addSelectedTenant": {
      "table": {
        "headers": {
          "tailNumber": "Tail #",
          "model": "Model",
          "currentLocation": "Current Location",
          "lastHangared": "Last Hangared",
          "type": "Type"
        }
      },
      "status": {
        "inHoldingArea": "In Holding Area",
        "inThisHangar": "In This Hangar",
        "inLocation": "In {{locationName}}"
      },
      "buttons": {
        "add": "Add"
      },
      "unassignedText": "Not Hangared"
    },
    "customizeHeatmap": {
      "loading": "Loading..."
    }
  },
  "stackingSOPs": {
    "titles": {
      "hangarStacking": "Hangar Stacking",
      "normalSpacing": "Normal Spacing",
      "looseSpacing": "Loose Spacing",
      "tightSpacing": "Tight Spacing",
      "rampSpacing": "Ramp Spacing"
    },
    "labels": {
      "horizontalSpacing": "Horizontal Spacing ({{unit}})",
      "verticalSpacing": "Vertical Spacing ({{unit}})",
      "allowOverlap": "Allow Overlap?"
    },
    "noseInThresholds": {
      "title": "Nose-In/Tail-In Settings",
      "tooltipText": "AirPlx uses the lower height measurement of the horizontal stabilizer to determine the default orientation when a new aircraft is added to a hangar. Use the slider below to adjust the horizontal stabilizer height at which AirPlx will default to Tail-in, No-Preset, or Nose-In. For example:",
      "tooltipExamples": {
        "allTailIn": "If you prefer to tail-in all aircraft, move both sliders all the way to the right.",
        "below20TailIn": "If you want all aircraft below 20{{unit}} to tail-in and all other to nose-in, move both sliders to 20{{unit}}."
      },
      "tooltipNote": "Note: You can adjust individual aircraft orientation inside each hangar as desired.",
      "orientations": {
        "tailIn": "Tail In",
        "noPreset": "No Preset",
        "noseIn": "Nose In"
      },
      "resetButton": "Reset to Default"
    },
    "tabs": {
      "baseWide": "Base-Wide SOPs",
      "locationOverrides": "Location Overrides"
    },
    "baseWide": {
      "description": "Configure default spacing and orientation settings that apply to all hangars and ramps. These can be overridden per location in the Location Overrides tab."
    },
    "overrides": {
      "name": "Name",
      "status": "Status",
      "actions": "Actions",
      "edit": "Edit",
      "overrideCount": "{{count}} override(s)",
      "usingBaseWide": "Using base-wide SOPs",
      "description": "Override base-wide SOP settings for specific hangars or ramps. Locations without overrides will use the base-wide settings.",
      "hangars": "Hangars",
      "ramps": "Ramps",
      "noHangars": "No hangars configured",
      "noRamps": "No ramps configured",
      "editTitle": "SOP Overrides for {{name}}",
      "editDescription": "Override base-wide SOP settings for this location. Leave fields blank to use the base-wide default.",
      "orientationThresholds": "Orientation Thresholds",
      "fboDefault": "FBO default: {{value}}",
      "resetAll": "Reset All",
      "saveSuccess": "SOP overrides saved successfully",
      "saveError": "Failed to save SOP overrides"
    }
  },
  "rampsSettings": {
    "buttons": {
      "addRamp": "Add Ramp",
      "modify": "Modify",
      "delete": "Delete",
      "copy": "Copy",
      "cancel": "Cancel"
    },
    "table": {
      "headers": {
        "name": "Ramp Name",
        "dimensions": "Dimensions"
      }
    },
    "copyDialog": {
      "title": "Copy Ramp",
      "fboLabel": "Copy to FBO",
      "copyButton": "Copy Ramp"
    },
    "deleteDialog": {
      "title": "Are you sure you want to delete {{rampName}}?",
      "message": "This ramp and all of its configuration will be deleted.",
      "confirmButton": "Delete Ramp"
    },
    "emptyState": {
      "title": "No Ramps Configured",
      "description": "Click 'Add Ramp' above to configure your first ramp."
    },
    "labels": {
      "rampName": "Ramp Name",
      "width": "Width ({{unit}})",
      "depth": "Depth ({{unit}})"
    },
    "rampEditorForm": {
      "editRamp": "Edit Ramp",
      "boundingBoxCoordinates": "Bounding Box Coordinates",
      "coordinates": {
        "topLeft": "Top-Left",
        "topRight": "Top-Right",
        "bottomRight": "Bottom-Right",
        "bottomLeft": "Bottom-Left"
      },
      "confirmations": {
        "saveChanges": {
          "message": "Save Changes",
          "title": "Save Changes"
        }
      },
      "messages": {
        "needCalibrationPoints": "Need Calibration Points"
      }
    },
    "rampEditor": {
      "title": "Edit Ramp",
      "saveSuccess": "Ramp changes saved.",
      "properties": "Properties",
      "rampVersion": "Ramp Version",
      "v2Algorithm": "V2 Algorithm",
      "editName": "Click to rename",
      "namePlaceholder": "Enter ramp name",
      "unsavedChanges": {
        "title": "Unsaved Changes",
        "message": "You have unsaved changes. Are you sure you want to exit?",
        "exitButton": "Exit Without Saving",
        "stayButton": "Stay"
      },
      "gisShapeEditGrid": {
        "warningOutOfBounds": "This shape may not be visible within the ramp's current bounding box",
        "placeholderShapeText": "Shape text",
        "presetLabel": "Preset",
        "strokeLabel": "Stroke",
        "fillLabel": "Fill",
        "doneButton": "Done",
        "noShapes": "No shapes to display",
        "instructions": "Click on text/label or font size to edit inline. Use the edit icon for colors and tags.",
        "applyDefaultStyling": "Apply Default Styling",
        "headers": {
          "textLabel": "Text/Label",
          "tags": "Tags",
          "colors": "Colors",
          "fontSize": "Font Size",
          "actions": "Actions"
        },
        "shapeCount": "Shape Count"
      },
      "toolbar": {
        "selectSpotSize": "Select spot size",
        "sections": {
          "options": "Options",
          "calibrationPoints": "Calibration Points"
        },
        "options": {
          "image": "Image",
          "measurements": "Measurements",
          "rampV2": "Ramp v2.0",
          "autoParking": "AutoParking",
          "guideLines": "Guide Lines",
          "showPoints": "Show points"
        },
        "tooltips": {
          "downloadImage": "Download ramp image",
          "uploadImage": "Upload new ramp image",
          "compressImage": "Compress existing image"
        },
        "tabs": {
          "tools": "Tools",
          "shapes": "Shapes",
          "hangars": "Hangars"
        },
        "buttons": {
          "finishedAdding": "Finished Adding",
          "addPoint": "Add Point"
        },
        "bulkOperations": "Bulk Operations",
        "noHangarsAvailable": "No hangars available",
        "spotSizes": {
          "verySmall": "Very Small (x40ft)",
          "small": "Small (x60ft)",
          "midsize": "Midsize (x80ft)",
          "superMid": "S-Mid (x100ft)",
          "large": "Large (x120ft)"
        },
        "snackbar": {
          "imageSaved": "Saved {{kb}}KB ({{percent}}%)",
          "compressionFailed": "Failed to compress image. See console for details."
        }
      },
      "referenceAircraft": {
        "toolButton": "Reference Aircraft",
        "toolTooltip": "Add a reference aircraft for scale comparison",
        "dialog": {
          "title": "Select Reference Aircraft",
          "description": "Choose an aircraft to display as a scale reference on the canvas. Drag to move, press R to rotate.",
          "searchLabel": "Search Aircraft",
          "currentAircraft": "Selected Aircraft",
          "dimensions": "{{wingspan}}ft wingspan x {{length}}ft length",
          "clearButton": "Remove Reference",
          "applyButton": "Apply",
          "cancelButton": "Cancel"
        },
        "hints": {
          "drag": "Drag to move",
          "rotate": "R to rotate, Shift+R counter-clockwise",
          "delete": "Delete to remove"
        }
      },
      "rampInitialize": {
        "title": "Create New Ramp",
        "rampNameLabel": "Ramp Name",
        "uploadImageButton": "Upload Ramp Reference Image",
        "calibrationInstructions": "Please create a reference line on the image. We'll use this to calibrate the scale of your ramp from pixels to {{unit}}.",
        "redoButton": "Redo",
        "confirmButton": "Looks Good",
        "continueButton": "Continue to Setup",
        "dropImageHint": "Drag & drop a ramp image here",
        "setupHint": "After uploading, you'll calibrate the image using GPS coordinates to set accurate dimensions."
      },
      "compassRoseExists": "Compass rose already exists on this ramp",
      "recalibrate": "Recalibrate GPS",
      "calibrationPointDialog": {
        "position": "Position: ({{x}}, {{y}})",
        "addButton": "Add Button",
        "cancelButton": "Cancel Button",
        "coordinatesLabel": "Coordinates Label",
        "coordinatesPlaceholder": "Enter Coordinates",
        "latitudeLabel": "Latitude Label",
        "longitudeLabel": "Longitude Label",
        "titleAdd": "Title Add",
        "titleEdit": "Title Edit",
        "updateButton": "Update Button"
      },
      "calibrationPointsList": {
        "noPoints": "No calibration points defined",
        "position": "Position"
      },
      "rampCanvasToolbar": {
        "typeLabel": "Type",
        "tools": {
          "measuringTool": "Measuring Tool",
          "modifyOutline": "Modify Outline",
          "addHazard": "Add Hazard",
          "unknown": "Unknown"
        },
        "tooltips": {
          "save": "Save",
          "delete": "Delete"
        },
        "markingTypes": {
          "whiteSolid": "White Solid",
          "whiteDashed": "White Dashed",
          "yellowSolid": "Yellow Solid",
          "yellowDashed": "Yellow Dashed",
          "redSolid": "Red Solid",
          "grey": "Grey"
        }
      },
      "spotTilePicker": {
        "hoverPrompt": "Hover over a spot to preview",
        "gridSize": "Grid: {{width}}x{{height}}"
      },
      "gisImport": {
        "title": "Import GIS Data",
        "sections": {
          "formatRequirements": "Format Requirements",
          "importedShapes": "Imported Shapes ({{count}})"
        },
        "format": {
          "delimiter": "<strong>Delimiter:</strong> Tab-separated (TSV)",
          "requiredColumns": "<strong>Required columns:</strong> geometry (WKT format)",
          "optionalColumns": "<strong>Optional columns:</strong> ramp_label, stroke_color, fill_color, font_size",
          "sampleNote": "Download the sample file below for reference."
        },
        "defaults": {
          "shapeName": "Imported Shape"
        },
        "buttons": {
          "cancel": "Cancel",
          "downloadSample": "Download Sample",
          "processCSV": "Process File",
          "import": "Import {{count}} Shapes"
        },
        "errors": {
          "calibrationPoints": "At least 2 calibration points are required for coordinate transformation",
          "processingFailed": "Failed to process file"
        }
      },
      "spotProperties": {
        "disabled": {
          "label": "Disabled",
          "tooltip": "When enabled, this spot will be excluded from AutoPark calculations"
        },
        "allowedSizes": {
          "label": "Allowed Aircraft Sizes",
          "tooltip": "Restrict which aircraft sizes can park in this spot. Smaller sizes are always allowed if larger sizes are permitted.",
          "spotSize": "Spot fits: ~{{feet}}ft wingspan ({{category}})",
          "tooLarge": "(too large)"
        },
        "egressLanes": {
          "label": "Egress Lanes",
          "tooltip": "Select which lanes this spot can use for egress. Aircraft will only be placed here if at least one egress lane is available.",
          "noneSelected": "None selected",
          "noLanesDefined": "No lanes defined on this ramp",
          "laneLabel": "Lane {{id}}"
        },
        "gapSpot": {
          "label": "Gap Spot",
          "tooltip": "Gap spots act as buffer zones between aircraft. When enabled, this spot cannot be used unless all required adjacent spots are occupied.",
          "checkboxLabel": "Enable as gap spot",
          "requiredSpots": "Required spots (must all be occupied):",
          "noneDetected": "No adjacent spots detected"
        },
        "maxDwellTime": {
          "label": "Max Dwell Time",
          "tooltip": "Maximum time an aircraft can remain in this spot (e.g., for quick-turn spots)",
          "inputLabel": "Max hours",
          "placeholder": "No limit",
          "helperText": "Leave empty for no limit"
        },
        "minDwellTime": {
          "label": "Min Dwell Time",
          "tooltip": "Minimum time an aircraft must remain in this spot (e.g., for overnight-only spots)",
          "inputLabel": "Min hours",
          "placeholder": "No minimum",
          "helperText": "Leave empty for no minimum"
        },
        "blockingSpots": {
          "label": "Blocking Relationships",
          "tooltip": "Shows which spots block this spot and which spots this spot blocks",
          "none": "No blocking relationships",
          "noBlocks": "Does not block any spots"
        },
        "tiling": {
          "label": "Spot Tiling",
          "tooltip": "Create a grid of spots based on this spot's dimensions",
          "button": "Create Tile Grid",
          "bufferLabel": "Buffer (ft)"
        }
      },
      "shapeEdit": {
        "mixedTypes": "Mixed shape types",
        "actions": {
          "delete": "Delete",
          "copy": "Copy",
          "cancel": "Cancel",
          "ok": "OK"
        },
        "algorithm": {
          "referencePoints": "Reference Points",
          "orientations": {
            "nose": "Nose",
            "tail": "Tail",
            "overhang": "Overhang",
            "center": "Center"
          },
          "refPoints": {
            "north": {
              "label": "North",
              "tooltip": "Reference point on the north side of the spot"
            },
            "south": {
              "label": "South",
              "tooltip": "Reference point on the south side of the spot"
            },
            "east": {
              "label": "East",
              "tooltip": "Reference point on the east side of the spot"
            },
            "west": {
              "label": "West",
              "tooltip": "Reference point on the west side of the spot"
            },
            "center": {
              "label": "Center",
              "tooltip": "Reference point at the center of the spot"
            }
          },
          "noValue": "—",
          "zoneProperties": "Zone Properties",
          "zoneDisabled": {
            "label": "Disabled",
            "tooltip": "When enabled, this zone will be excluded from AutoPark calculations"
          },
          "stagingArea": {
            "label": "Staging Area",
            "tooltip": "Mark this zone as a staging area for temporary aircraft positioning"
          },
          "overflowParking": {
            "label": "Overflow Parking",
            "tooltip": "Use this zone for overflow when primary spots are full"
          },
          "zoneName": {
            "label": "Zone Name",
            "placeholder": "Enter zone name",
            "helperText": "Optional display name for this zone"
          },
          "zonePriority": "Zone Priority",
          "zonePriorityHelp": "Higher priority zones are preferred for parking (0=lowest, 10=highest)",
          "aircraftOrientation": "Aircraft Orientation",
          "noseToLine": "Nose To Line",
          "priority": "Priority",
          "settings": "Settings",
          "tailToLine": "Tail To Line"
        },
        "properties": {
          "locked": "Locked",
          "tags": "Tags",
          "text": "Text"
        },
        "styling": {
          "color": "Color",
          "custom": "Custom",
          "fill": "Fill",
          "fontSize": "Font Size",
          "line": "Line",
          "lineStyle": "Line Style",
          "lineWidth": "Line Width",
          "pattern": "Pattern",
          "prefixedColors": "Prefixed Colors",
          "showText": "Show Text",
          "text": "Text",
          "textPosition": "Text Position"
        },
        "tabs": {
          "algorithm": "Algorithm",
          "properties": "Properties",
          "styling": "Styling"
        },
        "title": "Shape Edit"
      },
      "canvasTextInput": {
        "title": "Edit Text",
        "nameLabel": "Text",
        "cancel": "Cancel",
        "save": "Save"
      },
      "egressCalculation": {
        "close": "Close",
        "details": {
          "blocksLabel": "Blocks Label",
          "gapSpotNote": "Gap Spot Note",
          "title": "Details"
        },
        "spotAnalysis": {
          "blockedByTooltip": "Blocked By Tooltip",
          "blocksTooltip": "Blocks Tooltip",
          "headers": {
            "blockedByCount": "Blocked By Count",
            "blockingCount": "Blocking Count",
            "egressLane": "Egress Lane",
            "egressLaneTooltip": "Egress Lane Tooltip",
            "hasLane": "Has Lane",
            "spot": "Spot",
            "status": "Status"
          },
          "noLane": "No Lane",
          "title": "Spot Analysis",
          "zeroCount": "Zero Count"
        },
        "summary": {
          "blockingRelationships": "Blocking Relationships",
          "title": "Summary",
          "totalSpots": "Total Spots",
          "withEgressLane": "With Egress Lane",
          "withoutEgressLane": "Without Egress Lane"
        },
        "title": "Egress Calculation"
      },
      "textEdit": {
        "cancel": "Cancel",
        "color": "Color",
        "ok": "Ok",
        "size": "Size",
        "text": "Text",
        "title": "Text Edit"
      }
    },
    "rampCalibrationCanvas": {
      "referenceDistanceDialog": {
        "description": "Description",
        "distanceLabel": "Distance Label",
        "title": "Reference Distance Dialog"
      }
    },
    "setupWizard": {
      "complete": "Setup complete!",
      "calibrate": {
        "clickToPlace": "Click on the image to place calibration point {{label}}",
        "enterGPS": "Enter GPS coordinates for this point",
        "ready": "Ready to calibrate"
      },
      "confirm": {
        "finish": "Finish Setup"
      },
      "gps": {
        "title": "GPS Coordinates",
        "latitude": "Latitude",
        "longitude": "Longitude",
        "latError": "Latitude must be between -90 and 90",
        "lngError": "Longitude must be between -180 and 180",
        "emptyError": "Please enter coordinates"
      },
      "review": {
        "continue": "Continue"
      }
    }
  },
  "revenueEstimator": {
    "tabs": {
      "rates": "Rates",
      "feeCalculator": "Fee Calculator"
    },
    "migration": {
      "message": "The Fee Calculator can now be accessed via the side navigation bar. Click below to go to the Fee Calculator.",
      "button": "Go to Fee Calculator"
    },
    "rateCalculator": {
      "title": "Help Calculate My Current Rates",
      "subtitle": "If you don't know your typical monthly tenant or nightly transient rates, use this calculator to figure them out. Just enter an example aircraft and the total fee you currently charge to see what that equates to in monthly or daily rates.",
      "headers": {
        "aircraft": "Aircraft",
        "dimensions": "Length x Wingspan",
        "totalArea": "Total Area",
        "totalFee": "Total Fee",
        "pricePerArea": "$/{{unit}}"
      },
      "labels": {
        "makeModel": "Make/Model",
        "rate": "Rate"
      },
      "formats": {
        "dimensions": "{{length}}L x {{wingspan}}WS",
        "area": "{{area}} {{unit}}"
      }
    },
    "feeCalculator": {
      "headers": {
        "aircraft": "Aircraft",
        "dimensions": "Length x Wingspan",
        "buffer": "Buffer",
        "totalArea": "Total Area",
        "rate": "Rate",
        "cam": "CAM",
        "total": "Total",
        "totalWithCam": "Total + CAM"
      },
      "tooltips": {
        "buffer": "A buffer is added to each end of the dimensions. Eg: a 2ft buffer adds a total of 4ft to the length and 4ft to the wingspan.",
        "rate": "Enter your desired rates below. You can also change the default rates by going to Settings → Estimation→Rates.",
        "cam": "Common Area Maintenance Fees added to monthly tenant rate"
      },
      "labels": {
        "makeModel": "Make/Model",
        "buffer": "Buffer",
        "monthlyRate": "$ / {{unit}} / month",
        "dailyRate": "$ / {{unit}} / day",
        "tenantMonthly": "Tenant (monthly)",
        "transientDaily": "Transient (daily)"
      },
      "formats": {
        "totalArea": "{{area}} {{unit}}",
        "dimensions": "{{length}}L x {{wingspan}}WS",
        "camUnit": "$/{{unit}}²"
      }
    },
    "common": {
      "empty": "---"
    },
    "annualTenantTable": {
      "labels": {
        "annualIncrease": "Annual % Increase",
        "aircraftFee": "Aircraft Fee",
        "camFees": "CAM Fees",
        "totalFee": "Total Fee",
        "year": "Year {{number}}"
      }
    },
    "ratesForm": {
      "labels": {
        "label": "Label",
        "baseTenantRate": "Base Tenant Rate",
        "transientRate": "Transient Rate",
        "defaultLabel": "Default",
        "newRate": "New Rate",
        "defaultBuffer": "Default Buffer",
        "buffer": "Buffer",
        "defaultCAM": "Default Common Area Maintenance Fees",
        "or": "OR"
      },
      "buttons": {
        "addRate": "Add Rate"
      },
      "tooltips": {
        "buffer": "A buffer is added to each end of the dimensions. Eg: a {{value}}{{unit}} buffer adds a total of {{total}}{{unit}} to the length and {{total}}{{unit}} to the wingspan.",
        "cam": "Common Area Maintenance Fees added to monthly tenant rate. You can specify a percentage fee or a {{unit}}² fee."
      }
    }
  },
  "towingEquipment": {
    "headers": {
      "name": "Name",
      "length": "Length ({{unit}})",
      "width": "Width ({{unit}})",
      "height": "Height ({{unit}})",
      "towBarLength": "Tow Bar Length ({{unit}})"
    },
    "buttons": {
      "add": "Add",
      "remove": "Remove",
      "makeDefault": "Make Default"
    },
    "labels": {
      "default": "Default",
      "notAvailable": "N/A"
    }
  },
  "customizeHeatmap": {
    "title": "Heatmap",
    "sections": {
      "refPoints": "Ref Points",
      "preview": "Ref Points Preview"
    },
    "labels": {
      "refPointX": "Ref Point X",
      "refPointY": "Ref Point Y",
      "dimensions": "{{value}} ft"
    },
    "buttons": {
      "add": "Add",
      "preview": "Preview"
    }
  },
  "uberAdmin": {
    "executionMatrix": {
      "components": {
        "aircraft": "Aircraft",
        "obstacles": "Obstacles",
        "towingEquip": "Towing Equip"
      },
      "headers": {
        "component": "Component",
        "prod": "Prod",
        "worker1": "Worker1",
        "worker2": "Worker2"
      },
      "legend": {
        "error": "Error",
        "na": "Na",
        "skipped": "Skipped",
        "success": "Success"
      },
      "noDetails": "No Details",
      "title": "Execution Matrix"
    }
  }
}
